import {
  Anchor,
  BoxProps,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardIcon,
  CardTitle,
  Flex,
  Image,
  IntlRelativeTime,
  Stack,
  Text,
} from "@govlaunch/web";
import React, { ReactNode, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { IRelatedStoriesFieldsFragment } from "../fragments/__generated__/RelatedStoriesFieldsFragment.generated";
import { IMediaType } from "../graphql/types";
import { ArrowDownIcon } from "./ArrowIcons";

interface IStoryRelatedStoriesProps extends BoxProps {
  title?: string;
  relatedStories: IRelatedStoriesFieldsFragment[];
  placeholderImageSrc?: string;
  renderLinkWrap?: (story: IRelatedStoriesFieldsFragment, children: ReactNode) => ReactNode;
}

export default function StoryRelatedStories({
  title,
  relatedStories,
  renderLinkWrap,
  placeholderImageSrc,
  ...props
}: IStoryRelatedStoriesProps) {
  const intl = useIntl();
  const { items, showingMore, toggle } = useShowMore({
    items: relatedStories,
    initialCount: 2,
  });

  const cardTitle = title || intl.formatMessage({ defaultMessage: 'Related Stories', id: 'nuwuqz' });

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <Card {...props}>
      <CardHeader>
        <CardIcon viewBox="0 0 24 24" width="24px" height="24px">
          <RelatedStoriesIcon />
        </CardIcon>

        <CardTitle as="h2">{cardTitle}</CardTitle>
      </CardHeader>

      <CardBody>
        <Stack direction="column" mt={3} shouldWrapChildren={true}>
          {items.map((relatedStory, index) => {
            const storyCoverImage = (relatedStory.medias || []).find(({ type }) => type === IMediaType.Image);

            const children = (
              <Anchor
                key={relatedStory._id}
                borderTop={index === 0 ? null : "1px"}
                d="block"
                borderColor="gray.100"
                pt={index === 0 ? 0 : 6}
                mt={index === 0 ? 0 : 4}
              >
                {storyCoverImage && (
                  <Image
                    alt="story cover image"
                    src={`https://imaginary-ssl.govlaunch.com/resize?quality=100&compression=0&width=326&height=163&url=${storyCoverImage.url}`}
                    width="100%"
                    maxHeight="132px"
                    minHeight="132px"
                    objectFit="cover"
                    borderRadius="md"
                    mx="auto"
                    mb={4}
                  />
                )}

                {!storyCoverImage && placeholderImageSrc && (
                  <Image
                    alt="story cover placeholder image"
                    src={placeholderImageSrc}
                    width="100%"
                    borderRadius="md"
                    htmlWidth="100%"
                    htmlHeight="120px"
                    mx="auto"
                    mb={4}
                  />
                )}

                <Text fontSize="xs" color="gray.500" mt={4} mb={2} textDecoration="none !important">
                  <IntlRelativeTime
                    value={
                      relatedStory.lastPublishedAt || relatedStory.modifiedAt || relatedStory.createdAt || new Date()
                    }
                    hideOlderThan={{ unit: "month", value: 4 }}
                  />
                </Text>

                <Text as="h4" color="blue.600" fontWeight="bold" fontSize="xl" clamp={2} title={relatedStory.title}>
                  {relatedStory.title}
                </Text>

                <Text color="gray.900" fontSize="sm" clamp={2} m={0}>
                  {relatedStory.introduction}
                </Text>
              </Anchor>
            );

            return renderLinkWrap ? renderLinkWrap(relatedStory, children) : children;
          })}

          {relatedStories.length > 2 && !showingMore && (
            <Flex justify="center" mt={4}>
              <Button variant="secondary" size="sm" onClick={toggle} rightIcon={props => <ArrowDownIcon {...props} />}>
                <FormattedMessage defaultMessage="Show more" id="aWpBzj" />
              </Button>
            </Flex>
          )}
        </Stack>
      </CardBody>
    </Card>
  );
}

function RelatedStoriesIcon() {
  return (
    <>
      <path
        d="M23 0L12 2L1 0C0.4 0 0 0.4 0 1V21C0 21.6 0.4 22 1 22L12 24L23 22C23.6 22 24 21.6 24 21V1C24 0.4 23.6 0 23 0ZM11 21.8L2 20V18L11 20V21.8ZM11 19L2 17V2L11 3.8V19ZM22 20L13 21.8V20L22 18V20ZM22 17L13 19V3.8L22 2V17Z"
        fill="#086BE5"
      />
      <path d="M20 14L15 15V16L20 15V14Z" fill="#086BE5" />
      <path d="M20 11L15 12V13L20 12V11Z" fill="#086BE5" />
      <path d="M20 8L15 9V10L20 9V8Z" fill="#086BE5" />
      <path d="M20 5L15 6V7L20 6V5Z" fill="#086BE5" />
      <path d="M9.00024 15L4.00024 14V15L9.00024 16V15Z" fill="#086BE5" />
      <path d="M9.00024 12L4.00024 11V12L9.00024 13V12Z" fill="#086BE5" />
      <path d="M9.00024 9L4.00024 8V9L9.00024 10V9Z" fill="#086BE5" />
      <path d="M9.00024 6L4.00024 5V6L9.00024 7V6Z" fill="#086BE5" />
    </>
  );
}

interface IUseShowMoreArgs<T> {
  items: Array<T>;
  initialCount: number;
}

interface IUseShowMore<T> {
  items: Array<T>;
  showingMore: boolean;
  toggle: (arg?: any) => void;
}

function useShowMore<T>({ items, initialCount = 2 }: IUseShowMoreArgs<T>): IUseShowMore<T> {
  const [isShowingMore, setShowMore] = useState<boolean>(false);

  if (isShowingMore) {
    return {
      items,
      showingMore: true,
      toggle: () => setShowMore(false),
    };
  }

  return {
    items: items.slice(0, initialCount),
    showingMore: false,
    toggle: () => setShowMore(true),
  };
}
